'use client';

import { useEffect } from 'react';

// Minimal shape of Form.io's component registry (Components.components.*). Each
// patch below casts the entry it needs to its own typed class.
interface ComponentsRegistry {
  components?: Record<string, unknown>;
}

// Base component class (Components.components.component); all fields inherit createTooltip.
type CreateTooltip = (el: HTMLElement, settings?: Record<string, unknown>) => unknown;
// setElementInvalid(element, invalid) is called once per input ref to paint that field's validity;
// `type` is read on the instance to redirect a radio option's invalid state to its group instead.
type SetElementInvalid = (
  this: { type?: string },
  element: HTMLElement | undefined,
  invalid: boolean
) => unknown;
interface ComponentClass {
  prototype: {
    createTooltip?: CreateTooltip;
    setElementInvalid?: SetElementInvalid;
    __srpTooltipPatched?: boolean;
    __srpRadioValidityPatched?: boolean;
  };
}

// Marks a trigger whose label-forwarding default has already been suppressed.
interface TooltipTrigger extends HTMLElement {
  __srpLabelFocusSuppressed?: boolean;
}

// Radio component (SelectBoxes extends it and inherits attach()). attach() runs
// loadRefs first, so by the time it returns `refs.wrapper` holds one element per
// rendered option, each containing that option's <input> and <label>; `input` is the
// same options as native <input> elements.
interface RadioInstance {
  component?: { properties?: Record<string, string> };
  refs?: { wrapper?: HTMLElement[]; input?: HTMLInputElement[] };
  createTooltip: (el: HTMLElement) => unknown;
}
interface RadioClass {
  prototype: {
    attach?: (this: RadioInstance, element: HTMLElement) => unknown;
    setSelectedClasses?: (this: RadioInstance) => unknown;
    __srpOptionTooltipPatched?: boolean;
    __srpAriaCheckedPatched?: boolean;
  };
}

// Form.io's addEventListener(obj, type, func, persistent). ReviewPage registers
// a click handler through it during attach().
type AddEventListener = (
  obj: unknown,
  type: string,
  func: (...args: unknown[]) => unknown,
  persistent?: boolean
) => unknown;
// A ReviewPage row ({ path, data }); assignValue writes the rendered value onto it.
interface ReviewRow {
  path?: string;
  data?: unknown;
}
type AssignValue = (this: ReviewPageInstance, row: ReviewRow, component: unknown) => unknown;
interface ReviewPageInstance {
  addEventListener: AddEventListener;
  data?: Record<string, unknown>;
}
interface ReviewPageClass {
  prototype: {
    attach?: (this: ReviewPageInstance, element: HTMLElement) => unknown;
    assignValue?: AssignValue;
    __srpReviewPatched?: boolean;
  };
}

// File component (Components.components.file). A rejected file (wrong type / too
// big / duplicate name) is recorded only as an upload *status* on
// filesToSync.filesToUpload — never in the validation pipeline. checkComponentValidity
// returns the form field's validity (sync) or a Promise of it (async); the patch
// below forces it false while a rejected file is pending.
interface FileToSync {
  status?: string;
  message?: string;
}
interface FileInstance {
  filesToSync?: { filesToUpload?: FileToSync[] };
  component?: { key?: string };
  _errors?: unknown[];
  rootValue?: unknown;
  data?: unknown;
  t: (key: string, ctx?: Record<string, unknown>) => string;
  showValidationErrors: (
    errors: unknown[],
    data: unknown,
    row: unknown,
    flags: Record<string, unknown>
  ) => unknown;
}
interface FileClass {
  prototype: {
    checkComponentValidity?: (
      this: FileInstance,
      data?: unknown,
      dirty?: boolean,
      row?: unknown,
      flags?: Record<string, unknown>,
      allErrors?: unknown[]
    ) => boolean | Promise<boolean>;
    __srpFileRejectPatched?: boolean;
  };
}

// Button component (Components.components.button). Its onClick() calls
// triggerCaptcha() before the submit flow validates, so the premium CAPTCHA
// verification fires even on an invalid form. root.checkValidity(data, dirty,
// row, silentCheck) returns the whole form's validity; the silent variant used
// below decides whether to run the captcha without painting errors.
interface ButtonFormRoot {
  data?: unknown;
  checkValidity?: (data: unknown, dirty: boolean, row: unknown, silentCheck: boolean) => boolean;
}
interface ButtonInstance {
  component?: { action?: string };
  root?: ButtonFormRoot;
}

// Minimal shape for walking the form tree to spot pending file rejections. Every
// component instance exposes everyComponent; file components carry filesToSync.
interface RootWithEveryComponent {
  everyComponent?: (cb: (component: unknown) => void) => void;
}
interface ButtonClass {
  prototype: {
    triggerCaptcha?: (this: ButtonInstance) => unknown;
    __srpCaptchaGatePatched?: boolean;
  };
}

// Wizard (multi-page) display. Its Previous/Next/Submit bar is auto-generated —
// not authored Button components — so the button patch above never touches it.
// The submit-click reCAPTCHA (buttonKey 'submit', eventType 'buttonClick') is
// fired by Wizard.prototype.triggerButtonCaptcha from beforeSubmit(). Wizard's
// checkValidity signature is (data, dirty, row, currentPageOnly): on the last
// page (where the Submit button lives) it validates every page, and passes
// currentPageOnly through to each child as its silentCheck flag — so a truthy
// value gives an all-pages silent read.
interface WizardInstance {
  data?: unknown;
  checkValidity?: (data: unknown, dirty: boolean, row: unknown, currentPageOnly: boolean) => boolean;
  everyComponent?: (cb: (component: unknown) => void) => void;
}
interface WizardClass {
  prototype: {
    triggerButtonCaptcha?: (this: WizardInstance, page: unknown) => unknown;
    __srpWizardCaptchaGatePatched?: boolean;
  };
}
// Formio.Displays registry (Displays.displays.wizard holds the Wizard class).
interface DisplaysRegistry {
  displays?: Record<string, unknown>;
}

// Base component's translate function. Every component inherits t(); inside an
// edit-grid/data-grid row the instance carries a numeric rowIndex. Labels, error
// messages, and radio option labels all funnel their text through this.t(), so
// injecting an ordinal `rowIndex` interpolation variable here makes a single
// {{ rowIndex }} token resolve everywhere.
type TranslateFn = (this: TranslatableInstance, text: string, ...args: unknown[]) => string;
interface TranslatableInstance {
  rowIndex?: number;
}
interface TranslatableClass {
  prototype: {
    t?: TranslateFn;
    __srpRowOrdinalPatched?: boolean;
  };
}

/**
 * The tooltip trigger (<i>) is rendered inside the field's <label>. On touch
 * devices tippy toggles the tooltip on tap, but the tap's browser default action
 * — a <label> forwards the click to its labeled control — also fires, stealing
 * focus to the input so the tooltip never shows. Cancelling the default on the
 * trigger stops that forwarding while tippy's own (JS) click handler still
 * toggles. Radio/checkbox option labels aren't tied to one input, which is why
 * they were the only tooltips that already worked. Idempotent per trigger.
 */
function suppressLabelFocusOnTooltip(el: TooltipTrigger): void {
  if (el.__srpLabelFocusSuppressed) return;
  el.addEventListener('click', (event) => event.preventDefault());
  el.__srpLabelFocusSuppressed = true;
}

/**
 * Tooltips use tippy at placement:'right' with popper's 'absolute' strategy,
 * which grows the document (unsticks StickyTiles) and overflows on mobile. Keep
 * 'right' but make it viewport-safe (fixed + flip/preventOverflow). Patched on the
 * base component so every field — and the per-option triggers below — inherit it.
 * Idempotent; delegates to the original.
 */
function patchTooltipPlacement(registry: ComponentsRegistry): void {
  const Base = registry.components?.component as ComponentClass | undefined;
  const original = Base?.prototype?.createTooltip;
  if (!Base || !original || Base.prototype.__srpTooltipPatched) return;

  Base.prototype.createTooltip = function (
    this: unknown,
    el: HTMLElement,
    settings: Record<string, unknown> = {}
  ) {
    suppressLabelFocusOnTooltip(el);
    return original.call(this, el, {
      placement: 'right',
      popperOptions: {
        strategy: 'fixed',
        modifiers: [
          { name: 'flip', options: { fallbackPlacements: ['left', 'top', 'bottom'] } },
          { name: 'preventOverflow', options: { padding: 8 } },
        ],
      },
      ...settings,
    });
  };
  Base.prototype.__srpTooltipPatched = true;
}

/**
 * Injects a question-circle tooltip trigger into each radio / select-box option
 * that has one authored. Form.io has no OOTB per-option tooltip, so an author
 * adds a `tooltip:<optionValue>` entry to the field's Custom Properties (API tab)
 * whose value is an i18n key. Runs after the original attach() has populated the
 * option DOM. Idempotent — attach() re-runs on every redraw.
 */
function addOptionTooltips(instance: RadioInstance): void {
  const properties = instance.component?.properties ?? {};

  for (const wrapper of instance.refs?.wrapper ?? []) {
    // Read the option's value straight from its own input (template sets
    // value="{item.value}"), so we never rely on array-index alignment.
    const value = wrapper.querySelector('input')?.getAttribute('value');
    const key = value ? properties[`tooltip:${value}`] : undefined;
    if (!key) continue;

    const label = wrapper.querySelector<HTMLElement>('.form-check-label, label');
    // Skip when the label is missing or a trigger was already injected (redraw).
    if (!label || label.querySelector('[data-srp-option-tooltip]')) continue;

    // Append the trigger inside the label's text <span> so it flows right after
    // the option text (and wraps with it); fall back to the label itself.
    const target = label.querySelector<HTMLElement>('span') ?? label;

    const icon = document.createElement('i');
    icon.className = 'bi bi-question-circle';
    // Store the RAW i18n key — createTooltip resolves it via this.t() exactly
    // once (translating here too would double-resolve it).
    icon.setAttribute('data-tooltip', key);
    icon.setAttribute('data-srp-option-tooltip', '');
    icon.setAttribute('tabindex', '0'); // focus is a tippy trigger
    target.appendChild(icon);
    instance.createTooltip(icon); // reuses the patched placement + tippy styling
  }
}

/**
 * Wraps Radio.attach so option tooltips are added after each render. SelectBoxes
 * extends Radio and inherits attach(), so this single patch covers both.
 * Idempotent; delegates to the original.
 */
function patchRadioOptionTooltips(registry: ComponentsRegistry): void {
  const Radio = registry.components?.radio as RadioClass | undefined;
  const originalAttach = Radio?.prototype?.attach;
  if (!Radio || !originalAttach || Radio.prototype.__srpOptionTooltipPatched) return;

  Radio.prototype.attach = function (this: RadioInstance, element: HTMLElement) {
    const result = originalAttach.call(this, element);
    addOptionTooltips(this);
    return result;
  };
  Radio.prototype.__srpOptionTooltipPatched = true;
}

/**
 * @formio/premium's ReviewPage crashes whenever a review row references a
 * component the root can't resolve (hidden, nested, or missing): the lookup
 * returns undefined and two code paths use it unguarded. Guard both. Idempotent.
 */
function patchReviewPage(registry: ComponentsRegistry): void {
  const reviewProto = (registry.components?.reviewpage as ReviewPageClass | undefined)?.prototype;
  if (!reviewProto || reviewProto.__srpReviewPatched) return;

  // 1) assignValue calls component.getView() with no null check, throwing during
  // checkData on load. That uncaught error also aborts validation, so submit
  // stops scrolling to the first error. Fall back to the raw submission value (as
  // the original does) — this covers every call site at once.
  const originalAssignValue = reviewProto.assignValue;
  if (typeof originalAssignValue === 'function') {
    reviewProto.assignValue = function (
      this: ReviewPageInstance,
      row: ReviewRow,
      component: unknown
    ) {
      if (!component) {
        row.data = (row.path ? this.data?.[row.path] : undefined) ?? '';
        return undefined;
      }
      return originalAssignValue.call(this, row, component);
    };
  }

  // 2) attach()'s per-row click handler reads getComponent(path).dataValue before
  // null-checking, so a click with no resolvable data-path throws. Wrap the click
  // listeners it registers so the crash is swallowed; the real file-download path
  // still runs when the component resolves.
  const originalAttach = reviewProto.attach;
  if (typeof originalAttach === 'function') {
    reviewProto.attach = function (this: ReviewPageInstance, element: HTMLElement) {
      const originalAdd = this.addEventListener;
      // Shadow addEventListener only during attach() to wrap its click handlers.
      this.addEventListener = function (this: ReviewPageInstance, obj, type, func, persistent) {
        const wrapped =
          type === 'click' && typeof func === 'function'
            ? function (this: unknown, ...args: unknown[]) {
              try {
                return func.apply(this, args);
              } catch {
                // No resolvable component for this row — nothing to do.
                return undefined;
              }
            }
            : func;
        return originalAdd.call(this, obj, type, wrapped, persistent);
      };
      try {
        return originalAttach.call(this, element);
      } finally {
        // Drop the shadow; later lookups hit the prototype.
        delete (this as Partial<ReviewPageInstance>).addEventListener;
      }
    };
  }

  reviewProto.__srpReviewPatched = true;
}

/**
 * A rejected file (wrong type / too big / duplicate) lives only as an upload
 * *status* on the file component's filesToSync — it isn't part of the data
 * checkValidity validates, so a silent, non-dirty validity read can miss it.
 * Walk the form tree and report whether any file component is holding one.
 */
function hasPendingFileRejection(root: unknown): boolean {
  const form = root as RootWithEveryComponent | undefined;
  if (typeof form?.everyComponent !== 'function') return false;
  let found = false;
  form.everyComponent((component) => {
    const filesToUpload = (component as FileInstance | undefined)?.filesToSync?.filesToUpload;
    if (Array.isArray(filesToUpload) && filesToUpload.some((file) => file?.status === 'error')) {
      found = true;
    }
  });
  return found;
}

/**
 * Button.onClick() calls triggerCaptcha() before the form is validated, so the
 * premium CAPTCHA (reCAPTCHA) runs — and hits the server verify endpoint — even
 * when the submit is invalid. Gate triggerCaptcha for submit buttons behind a
 * silent validity check (plus the pending-file-rejection guard): if the form is
 * invalid we skip the captcha entirely and let the normal submit flow surface the
 * field errors; only a valid form runs verification. The check is synchronous +
 * silent (dirty:false, silentCheck:true) so it neither paints errors early nor
 * evaluates the token-dependent async captcha rule. Idempotent; delegates to the
 * original.
 */
function patchButtonCaptchaGating(registry: ComponentsRegistry): void {
  const Button = registry.components?.button as ButtonClass | undefined;
  const originalTrigger = Button?.prototype?.triggerCaptcha;
  if (!Button || !originalTrigger || Button.prototype.__srpCaptchaGatePatched) return;

  Button.prototype.triggerCaptcha = function (this: ButtonInstance) {
    if (this.component?.action === 'submit') {
      const root = this.root;
      const isValid = root?.checkValidity
        ? root.checkValidity(root.data, false, root.data, true)
        : true;
      // Invalid form (including a file component holding a rejected upload, which
      // a silent validity read can miss): skip captcha; the submit flow
      // re-validates and shows the errors.
      if (!isValid || hasPendingFileRejection(root)) return undefined;
    }
    return originalTrigger.call(this);
  };
  Button.prototype.__srpCaptchaGatePatched = true;
}

/**
 * The wizard equivalent of patchButtonCaptchaGating. Wizards trigger the
 * submit-click reCAPTCHA through Wizard.prototype.triggerButtonCaptcha (invoked
 * per page from beforeSubmit), which the Button patch can't reach because the
 * wizard's nav bar isn't built from Button components. Gate it behind the same
 * silent validity check plus the pending-file-rejection guard so the captcha
 * never verifies on an invalid submit; the normal submit flow then surfaces the
 * errors. Idempotent; delegates to the original.
 */
function patchWizardCaptchaGating(displays: DisplaysRegistry): void {
  const Wizard = displays.displays?.wizard as WizardClass | undefined;
  const original = Wizard?.prototype?.triggerButtonCaptcha;
  if (!Wizard || !original || Wizard.prototype.__srpWizardCaptchaGatePatched) return;

  Wizard.prototype.triggerButtonCaptcha = function (this: WizardInstance, page: unknown) {
    const isValid =
      typeof this.checkValidity === 'function'
        ? this.checkValidity(this.data, false, this.data, true)
        : true;
    if (!isValid || hasPendingFileRejection(this)) return undefined;
    return original.call(this, page);
  };
  Wizard.prototype.__srpWizardCaptchaGatePatched = true;
}

/**
 * Form.io's file component records a rejected file (wrong type, too big, or a
 * duplicate name) only as an *upload status* in filesToSync.filesToUpload — it
 * never reaches the validation pipeline, and only successful uploads land in the
 * component's value. So an optional file field holding nothing but a rejected
 * file still passes checkValidity and the form submits, leaving the red error
 * message behind. Surface those rejections as real component errors so submit is
 * blocked and the field joins scroll-to-first-error like any other invalid field.
 * The silent branch (used by the captcha gate) still reports invalid without
 * repainting, so an invalid submit also skips reCAPTCHA. Handles both the sync
 * and async validity paths. Idempotent; delegates to the original.
 */
function patchFileRejectionValidation(registry: ComponentsRegistry): void {
  const File = registry.components?.file as FileClass | undefined;
  const original = File?.prototype?.checkComponentValidity;
  if (!File || !original || File.prototype.__srpFileRejectPatched) return;

  File.prototype.checkComponentValidity = function (
    this: FileInstance,
    data,
    dirty,
    row,
    flags = {},
    allErrors = []
  ) {
    const rejected = (this.filesToSync?.filesToUpload ?? []).filter(
      (file) => file?.status === 'error'
    );

    const applyRejection = (valid: boolean): boolean => {
      if (!rejected.length) return valid;
      const error = {
        component: this.component,
        message: rejected[0]?.message ?? this.t('wrongFileType'),
        context: { key: this.component?.key },
        level: 'error',
      };
      allErrors.push(error);
      // Repaint this field's own errors plus the rejection, unless the caller
      // only wants a silent validity read (e.g. the captcha gate).
      if (!flags.silentCheck) {
        this.showValidationErrors(
          [...(this._errors ?? []), error],
          data ?? this.rootValue,
          row ?? this.data,
          flags
        );
      }
      return false;
    };

    const result = original.call(this, data, dirty, row, flags, allErrors);
    return result instanceof Promise ? result.then(applyRejection) : applyRejection(result);
  };
  File.prototype.__srpFileRejectPatched = true;
}

// Fallback ordinal words used only when no localized `rowOrdinal.<index>`
// translation resource is defined. Index 0 → "second" because, per the form's
// convention, the first property lives outside the grid (so the grid's first row
// is the second property). Prefer the resource so the words localize.
const FALLBACK_ROW_ORDINALS = [
  'second',
  'third',
  'fourth',
  'fifth',
  'sixth',
  'seventh',
  'eighth',
  'ninth',
  'tenth',
];

/**
 * Makes a reusable {{ rowIndex }} token in any authored, resource-backed string
 * (label, validation message, radio option label, …) resolve to a localized
 * ordinal word based on the component's position within an edit-grid/data-grid
 * row. Labels, errors, and option labels all render their text through the
 * component's t(); this wraps the base t() to inject an ordinal `rowIndex`
 * i18next interpolation variable whenever the instance is inside a row (numeric
 * rowIndex). The ordinal itself is looked up from a `rowOrdinal.<index>`
 * translation resource (so it localizes) with an English fallback. Components
 * outside a grid have no rowIndex, so they're untouched. Numeric rowIndex for
 * conditionals/logic is unaffected because that path uses evaluate(), not t().
 * Idempotent; delegates to the original.
 */
function patchRowIndexOrdinal(registry: ComponentsRegistry): void {
  const Base = registry.components?.component as TranslatableClass | undefined;
  const original = Base?.prototype?.t;
  if (!Base || !original || Base.prototype.__srpRowOrdinalPatched) return;

  Base.prototype.t = function (this: TranslatableInstance, text: string, ...args: unknown[]) {
    const index = this.rowIndex;
    if (typeof index === 'number') {
      const first = args[0];
      // Only augment when the first arg is the i18next options object (or absent);
      // never clobber a string defaultValue overload.
      if (first === undefined || (typeof first === 'object' && first !== null && !Array.isArray(first))) {
        const options = (first ?? {}) as Record<string, unknown>;
        // Resolve via the ORIGINAL t (not this patched one) to avoid recursion.
        const key = `rowOrdinal.${index}`;
        const resolved = original.call(this, key);
        options.rowIndex =
          resolved && resolved !== key
            ? resolved
            : (FALLBACK_ROW_ORDINALS[index] ?? String(index + 1));
        args[0] = options;
      }
    }
    return original.call(this, text, ...args);
  };
  Base.prototype.__srpRowOrdinalPatched = true;
}

/**
 * Form.io's Radio component (SelectBoxes inherits this) never sets aria-checked on its option
 * inputs — setSelectedClasses() only toggles the 'checked' attribute/class, so a screen reader
 * relying on an explicit role="radio" has no checked state to read (WCAG 4.1.2 Name, Role, Value).
 * Runs after every setSelectedClasses() call — attach() (initial render) and every value update —
 * so aria-checked always mirrors the input's live checked state. Scoped to radio-type inputs only;
 * SelectBoxes' checkbox inputs are untouched. Idempotent; delegates to the original.
 */
function patchRadioAriaChecked(registry: ComponentsRegistry): void {
  const Radio = registry.components?.radio as RadioClass | undefined;
  const original = Radio?.prototype?.setSelectedClasses;
  if (!Radio || !original || Radio.prototype.__srpAriaCheckedPatched) return;

  Radio.prototype.setSelectedClasses = function (this: RadioInstance) {
    const result = original.call(this);
    for (const input of this.refs?.input ?? []) {
      if (input.type === 'radio') {
        input.setAttribute('aria-checked', input.checked ? 'true' : 'false');
      }
    }
    return result;
  };
  Radio.prototype.__srpAriaCheckedPatched = true;
}

/**
 * Form.io's base Component.setElementInvalid() writes aria-invalid straight onto each radio option
 * input. Once that input carries an explicit role="radio", aria-invalid becomes a disallowed
 * attribute for that role (aria-allowed-attr) — validity belongs on the group, not each option. Move
 * it there instead: strip aria-invalid from the option input and mirror it onto its enclosing
 * role="radiogroup" wrapper (found via the rendered DOM, since the Radio component's own loadRefs
 * call never requests that container as a ref). Also fixes an asymmetry in Form.io's own code, which
 * sets aria-invalid="true" when a radio becomes invalid but never explicitly clears it back to
 * "false" once the field is fixed — this keeps the group's state correct in both directions.
 * Idempotent; delegates to the original.
 */
function patchRadioGroupValidity(registry: ComponentsRegistry): void {
  const Base = registry.components?.component as ComponentClass | undefined;
  const original = Base?.prototype?.setElementInvalid;
  if (!Base || !original || Base.prototype.__srpRadioValidityPatched) return;

  Base.prototype.setElementInvalid = function (
    this: { type?: string },
    element: HTMLElement | undefined,
    invalid: boolean
  ) {
    const result = original.call(this, element, invalid);
    if (this.type === 'radio' && element) {
      element.removeAttribute('aria-invalid');
      element
        .closest<HTMLElement>('[role="radiogroup"]')
        ?.setAttribute('aria-invalid', invalid ? 'true' : 'false');
    }
    return result;
  };
  Base.prototype.__srpRadioValidityPatched = true;
}

const FORMIO_DATE_INPUT_SELECTOR =
  "input.form-control[type='text'][placeholder*='MM-DD-YYYY'], input.form-control[type='text'][id*='dateOfBirth' i], input.form-control[type='text'][name*='dateOfBirth' i]";

const FORMIO_APN_INPUT_SELECTOR =
  "input.form-control[type='text'][id*='apn' i], input.form-control[type='text'][name*='apn' i], input.form-control[type='text'][placeholder*='APN' i]";

function normalizeDateSeparators(value: string): string {
  return value.replace(/-/g, '/');
}

function formatApnLikeShareholder(value: string): string {
  const cleaned = value.replace(/[^\dA-Za-z]/g, '');
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 5) return `${cleaned.slice(0, 3)}-${cleaned.slice(3)}`;
  if (cleaned.length <= 8) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 5)}-${cleaned.slice(5)}`;
  }
  return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 5)}-${cleaned.slice(5, 8)}-${cleaned.slice(8, 9)}`;
}

function patchFormioDateInputSeparators(): () => void {
  const isInBallotRequestForm = (input: HTMLInputElement): boolean => {
    const formRoot = input.closest('.formio-parent');
    return Boolean(
      formRoot?.querySelector('.ballot-request-table, .ballot-request-other-property-table')
    );
  };

  const normalizeInput = (input: HTMLInputElement) => {
    if (!isInBallotRequestForm(input)) return;
    const nextValue = normalizeDateSeparators(input.value);
    if (nextValue === input.value) return;
    input.value = nextValue;

    // Bubble an input event so Form.io state tracks the normalized value.
    input.dispatchEvent(new Event('input', { bubbles: true }));
  };

  const normalizeInputSettled = (input: HTMLInputElement) => {
    // Form.io can reapply mask formatting after native/input events.
    // Re-normalize on the next frame and next tick so slash formatting wins.
    normalizeInput(input);
    requestAnimationFrame(() => normalizeInput(input));
    setTimeout(() => normalizeInput(input), 0);
    setTimeout(() => normalizeInput(input), 30);
  };

  const normalizeAll = () => {
    document
      .querySelectorAll<HTMLInputElement>(FORMIO_DATE_INPUT_SELECTOR)
      .forEach((input) => {
        if (!isInBallotRequestForm(input)) return;
        normalizeInputSettled(input);
      });
  };

  const handleInputEvent = (event: Event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.matches(FORMIO_DATE_INPUT_SELECTOR)) return;
    if (!isInBallotRequestForm(target)) return;
    normalizeInputSettled(target);
  };

  document.addEventListener('input', handleInputEvent, true);
  document.addEventListener('change', handleInputEvent, true);
  document.addEventListener('blur', handleInputEvent, true);
  document.addEventListener('keyup', handleInputEvent, true);

  normalizeAll();

  const observer = new MutationObserver(() => {
    normalizeAll();
  });

  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  }

  return () => {
    document.removeEventListener('input', handleInputEvent, true);
    document.removeEventListener('change', handleInputEvent, true);
    document.removeEventListener('blur', handleInputEvent, true);
    document.removeEventListener('keyup', handleInputEvent, true);
    observer.disconnect();
  };
}

const FLATPICKR_DAY_SELECTOR = '.flatpickr-day';

// Adds the button role and disabled state flatpickr's own day markup omits (WCAG 4.1.2 Name, Role, Value)
function patchFlatpickrDayAccessibility(): () => void {
  const applyToDay = (day: Element) => {
    if (!(day instanceof HTMLElement)) return;
    day.setAttribute('role', 'button');
    day.setAttribute(
      'aria-disabled',
      day.classList.contains('flatpickr-disabled') ? 'true' : 'false'
    );
  };

  const applyAll = () => {
    document.querySelectorAll(FLATPICKR_DAY_SELECTOR).forEach(applyToDay);
  };

  applyAll();

  const observer = new MutationObserver(() => {
    applyAll();
  });

  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  }

  return () => {
    observer.disconnect();
  };
}

function patchFormioApnFormatting(): () => void {
  const normalizeInput = (input: HTMLInputElement) => {
    const nextValue = formatApnLikeShareholder(input.value);
    if (nextValue === input.value) return;
    input.value = nextValue;

    // Bubble an input event so Form.io state tracks the normalized value.
    input.dispatchEvent(new Event('input', { bubbles: true }));
  };

  const normalizeAll = () => {
    document
      .querySelectorAll<HTMLInputElement>(FORMIO_APN_INPUT_SELECTOR)
      .forEach((input) => normalizeInput(input));
  };

  const handleInputEvent = (event: Event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;
    if (!target.matches(FORMIO_APN_INPUT_SELECTOR)) return;
    normalizeInput(target);
  };

  document.addEventListener('input', handleInputEvent, true);
  document.addEventListener('change', handleInputEvent, true);
  document.addEventListener('blur', handleInputEvent, true);

  normalizeAll();

  const observer = new MutationObserver(() => {
    normalizeAll();
  });

  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  }

  return () => {
    document.removeEventListener('input', handleInputEvent, true);
    document.removeEventListener('change', handleInputEvent, true);
    document.removeEventListener('blur', handleInputEvent, true);
    observer.disconnect();
  };
}

export const FormioInit = () => {
  useEffect(() => {
    let cleanupDateSeparatorPatch: (() => void) | undefined;
    let cleanupApnFormatPatch: (() => void) | undefined;
    let cleanupFlatpickrA11yPatch: (() => void) | undefined;

    void (async () => {
      // Preload the core bundle and register premium components so forms render
      // correctly (and faster). License key comes from env, never hardcoded.
      const [formio, premiumModule, momentTz, tzData] = await Promise.all([
        import('@formio/js'),
        import('@formio/premium'),
        import('moment-timezone'),
        import('moment-timezone/data/packed/latest.json'),
      ]);
      momentTz.default.tz.load(tzData.default);
      const { Formio, Components, Displays } = formio;
      Formio.license = process.env.FORMIO_LICENSE_KEY ?? '';
      Formio.setBaseUrl(process.env.FORMIO_BASE_URL);
      Formio.setProjectUrl(process.env.FORMIO_PROJECT_URL);
      Formio.use(premiumModule.default ?? premiumModule);

      const registry = Components as unknown as ComponentsRegistry;
      patchTooltipPlacement(registry);
      patchRadioOptionTooltips(registry);
      patchReviewPage(registry);
      patchButtonCaptchaGating(registry);
      patchWizardCaptchaGating(Displays as unknown as DisplaysRegistry);
      patchFileRejectionValidation(registry);
      patchRowIndexOrdinal(registry);
      patchRadioAriaChecked(registry);
      patchRadioGroupValidity(registry);

      cleanupDateSeparatorPatch = patchFormioDateInputSeparators();
      cleanupApnFormatPatch = patchFormioApnFormatting();
      cleanupFlatpickrA11yPatch = patchFlatpickrDayAccessibility();
    })();

    return () => {
      cleanupDateSeparatorPatch?.();
      cleanupApnFormatPatch?.();
      cleanupFlatpickrA11yPatch?.();
    };
  }, []);
  return null;
};
